import { assertNever } from "@suse-o11y/assert-never";
import { Logger } from "@suse-o11y/logger";
import { StreamManager } from "@suse-o11y/stream-manager";

import {
	type ApiError,
	type StackPackStateChangeEvent,
	type StreamFailed,
	type SubjectPermissionsResponse,
	type ViewSnapshotError,
	type ViewSnapshotStreamResponse,
	WebSocketStreamType,
	isSubjectPermissionsResponse,
	isViewSnapshot,
	isViewSnapshotDiff,
	isViewSnapshotError,
} from "~/models/dto";
import { Permissions } from "~/models/permissions";

import { TopologySnapshot } from "../../app/state/topology/TopologySnapshot";

const logger = Logger.namespace("Stream Manager");

export const manager = StreamManager
	//
	.stream(WebSocketStreamType.ViewSnapshotDiffStream)
	.listen<ViewSnapshotStreamResponse, ViewSnapshotError, TopologySnapshot>((response, [, snapshot]) => {
		if (isViewSnapshotError(response)) {
			// on error keep last known topology state
			return [response, snapshot];
		}

		if (isViewSnapshot(response)) {
			return [null, TopologySnapshot.fromViewSnapshot(response)];
		}

		if (isViewSnapshotDiff(response)) {
			// this should never happen
			if (snapshot == null) {
				logger.warn("Received ViewSnapshotResponseDiff when ViewSnapshot is undefined");

				return [null, snapshot];
			}

			return [null, TopologySnapshot.applyDiff(response, snapshot)];
		}

		return assertNever(response);
	})

	.stream(WebSocketStreamType.PermissionStream)
	.listen<SubjectPermissionsResponse | ApiError, ApiError, Permissions>((response, [, permissions]) => {
		logger.info("Received SubjectPermissionsResponse", response);

		if (isSubjectPermissionsResponse(response)) {
			return [null, Permissions.init(response)];
		}

		return [response, permissions];
	})

	.stream(WebSocketStreamType.StatusUpdateStream)
	.listen<StackPackStateChangeEvent | StreamFailed, null, null>(() => [null, null]);
